import {
  Card,
  Typography,
  Box,
  Stack,
  Link as MuiLink,
  Grid2,
} from '@mui/material';
import { MailOutline, PhoneOutlined } from '@mui/icons-material';
import { PieChart } from '@mui/x-charts/PieChart';
import Button from '../../../components/ui/button';
import Session from '../../../assets/SessionIllustration.svg';
import { useEffect, useState } from 'react';
import { callAPI, formatDate } from '../../../components/utils';
import DataTable from '../../../components/table/dataTable';
import { useSidebarContext } from '../../../context/SidebarContext';
import ManageSessionModal from '../../../components/table/modals/modifySessionModal';

const cardStyle = {
  borderRadius: '10px',
  border: '1px solid #D1D5DB',
  boxShadow: '0.5px 1px 1px rgba(0, 0, 0, 0.1)',
  padding: '1.5rem',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  height: '100%',
};

const InfoCard = ({ title, description, children }) => (
  <Card sx={cardStyle}>
    <Stack gap={0.6}>
      <Typography variant="h4" fontWeight="600">
        {title}
      </Typography>
      <Typography variant="body2" sx={{ fontSize: '1.15rem' }}>
        {description}
      </Typography>
    </Stack>
    {children}
  </Card>
);

const LogSessionCard = ({ name, enabled, setEnabled }) => (
  
  <Card
    sx={{
      ...cardStyle,
      padding: '0',
      flexDirection: 'row',
    }}
  >
    <Grid2
      container
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
      }}
    >
      <Grid2
        item
        size={5}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          padding: '1.5rem',
        }}
      >
        <Stack direction="column" spacing={0.5}>
          <Typography variant="h3" sx={{ fontSize: '1.65rem' }}>
            Hello, {name || 'User'}!
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: '1.25rem', whiteSpace: 'nowrap' }}
          >
            Log your most recent session
          </Typography>
        </Stack>
        <Button variant="contained" sx={{ width: '150px', fontSize: '1.05rem' }} onClick={() => setEnabled(!enabled)}>
          + Log Session
        </Button>
        {enabled && (
        <ManageSessionModal 
          data={{reloadOnSuccess: false}} 
          callbacks={{ 
            onClose: () => setEnabled(false),
            updateRow: () => {} 
          }} 
          mode="create" 
        />
      )}
      </Grid2>

      <Grid2
        item
        size={6}
        sx={{
          display: { sm: 'none', md: 'flex' },
          '@media (max-width: 10px)': { display: 'none' },
          justifyContent: 'flex-end',
          alignItems: 'flex-end',
        }}
      >
        <img
          src={Session}
          alt="Session Image"
          style={{ minWidth: '550px', width: '110%', height: 'auto' }}
        />
      </Grid2>
    </Grid2>
  </Card>
);

const PieChartCard = ({ level, role, completed, total, lastUpdated }) => (
  <Card sx={{ ...cardStyle, height: '270px' }}>
    <Stack direction="row" justifyContent="space-between">
      <Stack>
        <Typography variant="h4" fontWeight="600">
          {level}
        </Typography>
        <Typography variant="h5" color="var(--gray-600)">
          {role}
        </Typography>
      </Stack>
      <Typography
        variant="body2"
        color="var(--gray-600)"
        sx={{ textAlign: 'right' }}
      >
        Last Updated <br /> {lastUpdated}
      </Typography>
    </Stack>

    {/* Pie Chart */}
    <Box
      sx={{ position: 'relative', display: 'flex', justifyContent: 'center' }}
    >
      <PieChart
        series={[
          {
            data: [
              {
                id: 0,
                label: 'Completed Sessions',
                value: completed,
                color: 'var(--blue-dark)',
              },
              {
                id: 1,
                label: 'Remaining Sessions',
                value: total - completed,
                color: 'var(--blue-light)',
              },
            ],
            innerRadius: 55,
            outerRadius: 75,
            paddingAngle: 2,
            cornerRadius: 3,
          },
        ]}
        height={150}
        slotProps={{ legend: { hidden: true } }}
        margin={{ right: 0 }}
      />

      {/* Centered Text */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
        }}
      >
        <Typography variant="h3" fontWeight="600">
          {completed}/{total}
        </Typography>
        <Typography variant="body1" fontWeight="500" color="var(--gray-500)">
          Sessions
        </Typography>
      </Box>
    </Box>

    {/* Legend */}
    <Stack direction="column" spacing={0.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          sx={{
            width: 12,
            height: 12,
            bgcolor: 'var(--blue-dark)',
            borderRadius: 1,
          }}
        />
        <Typography variant="body1">Completed Sessions</Typography>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          sx={{
            width: 12,
            height: 12,
            bgcolor: 'var(--blue-light)',
            borderRadius: 1,
          }}
        />
        <Typography variant="body1">Remaining Sessions</Typography>
      </Stack>
    </Stack>
  </Card>
);

const TutorDashboard = () => {
  const { isSidebarOpen } = useSidebarContext();
    const [enabled, setEnabled] = useState(false);

  const [userData, setUserData] = useState({
    name: 'No name found',
    tutorLevel: 'No level found',
    tutorLevelName: 'No level found',
    completedSessions: 0,
    totalSessions: 0,
    lastUpdated: '-',
  });

  useEffect(() => {
    const getUsers = async () => {
      await callAPI({
        request: 'getUsers',
        method: 'get',
        params: {
          self: true,
        },
        onSuccess: (response) => {
          const user = response.data;
          console.log(response.data);
          setUserData({
            name: user.name ?? '',
            tutorLevel: user.level ?? '',
            tutorLevelName: user.levelName ?? '',
            completedSessions: user.xp ?? '',
            totalSessions:
              user.xp && user.xpRequired
                ? Number(user.xp) + Number(user.xpRequired)
                : '0',
            lastUpdated: user.latestSessionSubmitedTime ?? '',
          });
        },
        onError: (errorMessage) => {
          console.error('Failed to fetch user data:', errorMessage);
        },
      });
    };
    getUsers();
  }, []);

  return (
    <Grid2
      container
      spacing={3}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100%',
        width: `calc(100vw - ${isSidebarOpen ? 220 : 80}px - 4rem)`,
      }}
    >
      {/* First Row: Log Session + Need Help */}
      <Grid2 container spacing={3}>
        {/* Log Session Card */}
        <Grid2
          item
          size={{ xs: 12, md: 8.5 }}
          sx={{ display: 'flex', flexDirection: 'column' }}
        >
          <LogSessionCard name={userData.name} enabled={enabled} setEnabled={setEnabled} />
        </Grid2>

        {/* Need Help Card */}
        <Grid2
          item
          size={{ xs: 12, md: 3.5 }}
          sx={{ display: 'flex', flexDirection: 'column' }}
        >
          <InfoCard
            title="Need Help?"
            description="Contact our Admin Team for any enquiries:"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneOutlined sx={{ color: 'var(--navyBlue-light)' }} />
              <Typography variant="body2" sx={{ fontSize: '1.15rem' }}>
                0480 041 747
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MailOutline sx={{ color: 'var(--navyBlue-light)' }} />
              <MuiLink
                href="mailto:Enquiries@tmjtutoring.com.au"
                sx={{
                  textDecoration: 'none',
                  fontSize: '1.15rem',
                  color: 'var(--black)',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Enquiries@tmjtutoring.com.au
              </MuiLink>
            </Box>
          </InfoCard>
        </Grid2>
      </Grid2>

      {/* Second Row: Log Data + Tutor Level Chart */}
      <Grid2 container spacing={3}>
        <Grid2
          item
          size={{ xs: 12, md: 8.5 }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '100%',
            overflowX: 'auto',
          }}
        >
          <Card sx={{ ...cardStyle, height: '270px' }}>
            <Stack direction="row">
              <Typography variant="h4" fontWeight="600">
                Recent Logs
              </Typography>
            </Stack>

            <Box className="dashboard-table">
              <DataTable tableId={4} simpleView={true} />
            </Box>
          </Card>
        </Grid2>

        {/* Tutor Level Chart */}
        <Grid2
          item
          size={{ xs: 12, md: 3.5 }}
          sx={{ display: 'flex', flexDirection: 'column' }}
        >
          <PieChartCard
            level={`Tutor Level ${userData.tutorLevel}`}
            role={userData.tutorLevelName}
            completed={userData.completedSessions}
            total={userData.totalSessions}
            lastUpdated={formatDate(userData.lastUpdated)}
          />
        </Grid2>
      </Grid2>
    </Grid2>
  );
};

export default TutorDashboard;
